import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { SpHttpCommonService, SpHttpServiceResponse } from 'src/app/services/sp-http-common.service';

export interface CeoV2Result<T> {
  data: T | null;
  /** Ready to show (`CODE: message`), or null on success. */
  error: string | null;
}

/**
 * The v2 engine's CEO API — on `totalum-accounts` since plan 23 N7 (`<builder-api>/api/v1/ceo/*`: the same paths
 * account-backend served under `ceo-dashboard/`, where every v2 setting now lives on Cloudflare). Account-backend only
 * signs the CEO in: `GET ceo-dashboard/v2/token` answers a 12 h token and the host, kept until 5 minutes before it
 * expires. Reads relay the engine's `{errors, data}` envelope verbatim; every write carries `{password, reason}` and
 * leaves an audit row.
 *
 * The write password is kept in memory for the tab only — typed once per session, never stored.
 */
@Injectable({ providedIn: 'root' })
export class CeoV2Service {
  password = '';
  private token: { token: string; url: string; expiresAt: number } | null = null;

  constructor(private http: SpHttpCommonService, private client: HttpClient) {}

  async get<T>(path: string, query?: Record<string, unknown>): Promise<CeoV2Result<T>> {
    let params = new HttpParams();
    for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
    return this.send<T>('GET', path, undefined, params);
  }

  async write<T>(method: 'POST' | 'PUT' | 'PATCH', path: string, body: object): Promise<CeoV2Result<T>> {
    return this.send<T>(method, path, body);
  }

  private async send<T>(method: string, path: string, body?: object, params?: HttpParams): Promise<CeoV2Result<T>> {
    // a valid token is used at once, so the request leaves in the same tick as the call (no await before it)
    const auth = this.fresh() ?? (await this.ceoToken());
    if (!auth) return { data: null, error: 'CEO_TOKEN_UNAVAILABLE: account-backend did not issue a CEO token; sign in again' };
    try {
      const r: any = await this.client
        .request(method, `${auth.url}/api/v1/ceo/${path}`, {
          body,
          params,
          headers: new HttpHeaders({ authorization: `Bearer ${auth.token}` }),
        })
        .toPromise();
      return { data: (r?.data ?? null) as T | null, error: null };
    } catch (e: any) {
      if (e?.status === 401 && e?.error?.errors?.errorCode === 'CEO_TOKEN_INVALID') this.token = null;
      return unwrap<T>({ success: undefined, error: e?.error ?? e });
    }
  }

  private fresh() {
    return this.token && this.token.expiresAt - Date.now() > 300_000 ? this.token : null;
  }

  /** The 12 h CEO token from account-backend (plan 23 §9.4, D7), reused until 5 minutes before it expires. */
  private async ceoToken() {
    const r = await this.http.httpGet(`${environment.apiDomain}api/v1/ceo-dashboard/v2/token`, new HttpParams());
    const d = r.success?.data;
    this.token = d?.token ? { token: d.token, url: String(d.url).replace(/\/+$/, ''), expiresAt: Date.parse(d.expiresAt) } : null;
    return this.token;
  }
}

/**
 * The refusal as the pages show it (`CODE: message`). An engine that is down answers `503 ENGINE_UNAVAILABLE`.
 */
export function unwrap<T>(r: SpHttpServiceResponse): CeoV2Result<T> {
  if (r.error === undefined) return { data: (r.success?.data ?? null) as T | null, error: null };
  const e = r.error?.errors ?? r.error?.error?.errors;
  if (typeof e === 'string') return { data: null, error: e };
  if (e?.errorCode) return { data: null, error: `${e.errorCode}: ${e.errorMessage ?? ''}`.trim() };
  return { data: null, error: r.error?.message || 'Request failed' };
}
